import { NoSymbolIcon } from '@heroicons/react/20/solid';
import { numberToLocale } from '@forms/client/AddProductForm/utils';
import { productPlaceholderImage } from '../../../utils/inventoryImage';
import { SkuActionsMenu, SkuMenuOption } from './SkuActionsMenu';
import {
  ProductGroupListing,
  productGroupDimensions,
  productGroupId,
  productGroupKey,
  productGroupName,
  productGroupTemperatureControl,
  productGroupWeight,
  productThumbnailSrc,
} from './productGroups';

export interface ProductCardGridProps {
  groups: ProductGroupListing[];
  isWarehouseUser: boolean;
  onOpenProduct: (group: ProductGroupListing) => void;
  buildMenuOptions: (group: ProductGroupListing) => SkuMenuOption[];
}

export function ProductCardGrid({
  groups,
  isWarehouseUser,
  onOpenProduct,
  buildMenuOptions,
}: ProductCardGridProps) {
  const detailRow = (label: string, value: string) => (
    <div className='flex items-baseline justify-between gap-2 text-sm'>
      <span className='shrink-0 text-gray-500'>{label}</span>
      <span className='truncate text-right text-gray-900'>{value}</span>
    </div>
  );

  return (
    <div className='flex flex-row flex-wrap gap-4'>
      {groups.map((group) => {
        const name = productGroupName(group);
        const thumbnailSrc = productThumbnailSrc(group);
        const productId = productGroupId(group);
        const areAllSkusArchived =
          group.skus.length > 0 && group.skus.every((sku) => sku.isArchived);

        return (
          <div
            key={productGroupKey(group)}
            role='button'
            tabIndex={0}
            className='group relative flex flex-col bg-white border border-gray-200 rounded-lg overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-gray-300 w-[220px]'
            onClick={() => onOpenProduct(group)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                onOpenProduct(group);
              }
            }}
            aria-label={`Open details for ${name}`}
          >
            <div className='relative aspect-square bg-gray-50'>
              <img
                src={thumbnailSrc ?? productPlaceholderImage(true)}
                alt={name}
                className={`w-full h-full ${
                  thumbnailSrc ? 'object-cover' : 'object-contain p-4'
                }`}
              />
              {isWarehouseUser && (
                <div className='absolute top-2 right-2 z-10'>
                  <SkuActionsMenu
                    id={productId ?? name}
                    options={buildMenuOptions(group)}
                  />
                </div>
              )}
              {areAllSkusArchived && (
                <NoSymbolIcon
                  title='Product is archived'
                  className='absolute top-12 right-2 z-10 h-6 w-6 shrink-0'
                  color='red'
                />
              )}
            </div>

            <div className='flex flex-col gap-1.5 p-3 border-t border-gray-100'>
              <h3 className='text-base font-semibold text-gray-900 truncate'>
                {name}
              </h3>
              <div className='flex items-baseline justify-between gap-2 text-sm'>
                <span className='shrink-0 text-gray-500'>Product ID</span>
                {productId ? (
                  <span className='truncate text-right text-gray-900'>
                    {productId}
                  </span>
                ) : (
                  <span
                    className='truncate text-right text-gray-400'
                    title='These SKUs have no product ID yet — edit one and set its product to group them.'
                  >
                    Not yet grouped
                  </span>
                )}
              </div>
              {detailRow('Weight', productGroupWeight(group))}
              {detailRow('Dimensions', productGroupDimensions(group))}
              {detailRow(
                'Temp. Control',
                productGroupTemperatureControl(group)
              )}
              {detailRow('SKUs', numberToLocale(group.skuCount))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
